import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  isAdminLoading: boolean;
  isPasswordRecovery: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // Check if user is admin
  const checkAdminStatus = async (userId: string | undefined) => {
    if (!userId) {
      setIsAdmin(false);
      return;
    }

    setIsAdminLoading(true);
    try {
      const { data, error } = await supabase
        .from('admins')
        .select('id')
        .eq('id', userId)
        .single();

      setIsAdmin(!error && !!data);
    } catch (err) {
     setIsAdmin(false);
     console.error('[AuthProvider]', 'checkAdminStatus error:', err);
    } finally {
      setIsAdminLoading(false);
    }
  };

  useEffect(() => {
    // Check URL hash on initial load for password recovery
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setIsPasswordRecovery(true);
      // Clear the hash to prevent re-detection
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Initial session check
    const initAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        // Check admin status
        await checkAdminStatus(initialSession?.user?.id);
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Setup listener for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, currentSession) => {
        // Detect password recovery event
        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }

        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);

        // Check admin status on auth change
        checkAdminStatus(currentSession?.user?.id);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setIsPasswordRecovery(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value = useMemo(() => ({
    session, user, isLoading, isAdmin, isAdminLoading, isPasswordRecovery, signOut
  }), [session, user, isLoading, isAdmin, isAdminLoading, isPasswordRecovery]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
